import { inject } from '@angular/core';
import {
  Router,
  CanActivateFn,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
} from '@angular/router';
import { AuthService } from '../services/auth.service';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, map, take } from 'rxjs/operators';

/**
 * Authentication Guard - Protects routes that require authentication
 *
 * @description
 * Functional route guard that prevents unauthenticated users from accessing protected routes.
 * Implements the CanActivateFn interface for Angular's modern functional guard pattern.
 *
 * When a user attempts to access a protected route without being authenticated:
 * 1. Checks authentication status via AuthService.isAuthenticated()
 * 2. If authenticated: allows route activation
 * 3. If not authenticated: redirects to /login with returnUrl query parameter
 *
 * The returnUrl parameter enables automatic redirect back to the originally requested
 * route after successful login.
 *
 * @param route - The route being activated
 * @param state - Router state snapshot containing the requested URL
 *
 * @returns true if user is authenticated, UrlTree for redirect if not authenticated
 *
 * @usage
 * ```typescript
 * // In route configuration (app.routes.ts):
 * export const routes: Routes = [
 *   {
 *     path: 'dashboard',
 *     component: DashboardComponent,
 *     canActivate: [authGuard]  // Protect this route
 *   },
 *   {
 *     path: 'resources',
 *     loadChildren: () => import('./features/resources/resources.module'),
 *     canActivate: [authGuard]  // Protect lazy-loaded module
 *   }
 * ];
 * ```
 *
 * @example
 * User navigates to /resources without being logged in:
 * 1. authGuard executes
 * 2. isAuthenticated() returns false
 * 3. User redirected to /login?returnUrl=%2Fresources
 * 4. After successful login, LoginComponent reads returnUrl and navigates to /resources
 *
 * @security
 * - Provides client-side route protection only
 * - Server-side protection enforced by RLS policies on database
 * - Does not expose sensitive data in route parameters
 * - returnUrl is preserved for better user experience
 *
 * @see {@link AuthService} for authentication state management
 * @see {@link authInterceptor} for handling 401 errors on API calls
 */
export const authGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot,
) => {
  const authService = inject(AuthService);
  const router = inject(Router);

  // Wait for auth initialization to complete
  return toObservable(authService.isLoading).pipe(
    filter((isLoading) => !isLoading),
    take(1),
    map(() => {
      // Check if user is authenticated
      if (authService.isAuthenticated()) {
        return true;
      }

      // Store the attempted URL for redirecting after login
      const returnUrl = state.url;

      // Redirect to login with returnUrl as query parameter
      return router.createUrlTree(['/login'], {
        queryParams: { returnUrl },
      });
    })
  );
};
